import { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  WhiteBoardS,
  ButtonS,
  ToolbarS,
  ZoomBarS,
  ColorBarS,
  ToolbarItemS,
  RangeInputS,
  ColorButtonS,
  SeparatorS,
  ToolbarHolderS,
  PDFWrapperS,
} from './Whiteboard.styled';
import { FabricImage } from 'fabric';
import { PdfReader } from '../PdfReader';
import { saveAs } from 'file-saver';
import { Board, modes } from './Board.Class.js';
import { ColorPicker } from '../ColorPicker';

import SelectIcon from './../images/cross.svg';
import EraserIcon from './../images/eraser.svg';
import TextIcon from './../images/text.svg';
import RectangleIcon from './../images/rectangle.svg';
import LineIcon from './../images/line.svg';
import EllipseIcon from './../images/ellipse.svg';
import TriangleIcon from './../images/triangle.svg';
import PencilIcon from './../images/pencil.svg';
import DeleteIcon from './../images/delete.svg';
import ZoomInIcon from './../images/zoom-in.svg';
import ZoomOutIcon from './../images/zoom-out.svg';
import DownloadIcon from './../images/download.svg';
import UploadIcon from './../images/add-photo.svg';
import FillIcon from './../images/color-fill.svg';

const initFileInfo = {
  file: { name: 'whiteboard' },
  totalPages: 1,
  currentPageNumber: 0,
  currentPage: '',
};

const initDrawingSettings = {
  currentMode: 'PENCIL',
  currentColor: '#000000',
  brushWidth: 5,
  fill: false,
};

const initSettings = {
  zoom: 1,
  contentJSON: null,
};

const Whiteboard = ({
  controls,
  settings,
  drawingSettings,
  fileInfo,
  onObjectAdded = () => {},
  onObjectRemoved = () => {},
  onObjectModified = () => {},
  onCanvasRender = () => {},
  onCanvasChange = () => {},
  onZoom = () => {},
  onImageUploaded = () => {},
  onPDFUploaded = () => {},
  onPDFUpdated = () => {},
  onPageChange = () => {},
  onOptionsChange = () => {},
  onSaveCanvasAsImage = () => {},
  onConfigChange = () => {},
  onSaveCanvasState = () => {},
}) => {
  const [board, setBoard] = useState();
  const [canvasObjectsPerPage, setCanvasObjectsPerPage] = useState({});
  const [canvasDrawingSettings, setCanvasDrawingSettings] = useState({
    ...initDrawingSettings,
    ...drawingSettings,
  });
  const canvasConfig = { ...initSettings, ...settings };
  const [zoom, setZoom] = useState(canvasConfig.zoom);
  const [fileReaderInfo, setFileReaderInfo] = useState({ ...initFileInfo, ...fileInfo });
  const canvasRef = useRef(null);
  const whiteboardRef = useRef(null);
  const uploadPdfRef = useRef(null);

  // The canvas listeners are attached once, so they read callbacks and state
  // through a ref to avoid capturing the values of the first render.
  const latestRef = useRef(null);
  latestRef.current = {
    canvasDrawingSettings,
    fileReaderInfo,
    onObjectAdded,
    onObjectRemoved,
    onObjectModified,
    onCanvasRender,
    onCanvasChange,
    onZoom,
    onConfigChange,
  };

  const enabledControls = useMemo(
    function () {
      return {
        [modes.PENCIL]: true,
        [modes.LINE]: true,
        [modes.RECTANGLE]: true,
        [modes.ELLIPSE]: true,
        [modes.TRIANGLE]: true,
        [modes.TEXT]: true,
        [modes.SELECT]: true,
        [modes.ERASER]: true,
        CLEAR: true,
        FILL: true,
        BRUSH: true,
        COLOR_PICKER: true,
        DEFAULT_COLORS: true,
        FILES: true,
        SAVE_AS_IMAGE: true,
        GO_TO_START: true,
        ZOOM: true,

        ...controls,
      };
    },
    [controls],
  );

  // Merge incoming prop updates during render rather than in an effect, which is
  // React's recommended way to adjust state when a prop changes.
  const [prevDrawingSettings, setPrevDrawingSettings] = useState(drawingSettings);
  if (drawingSettings !== prevDrawingSettings) {
    setPrevDrawingSettings(drawingSettings);
    setCanvasDrawingSettings((current) => ({ ...current, ...drawingSettings }));
  }

  const [prevFileInfo, setPrevFileInfo] = useState(fileInfo);
  if (fileInfo !== prevFileInfo) {
    setPrevFileInfo(fileInfo);
    setFileReaderInfo((current) => ({ ...current, ...fileInfo }));
  }

  useEffect(() => {
    if (!board) return;
    board.setCanvasConfig(canvasConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, board]);

  // Create the fabric board exactly once. Returning the disposer keeps React 19's
  // StrictMode remount from leaving a second canvas bound to the same element.
  useEffect(() => {
    const newBoard = new Board({
      canvasElement: canvasRef.current,
      drawingSettings: { ...initDrawingSettings, ...drawingSettings },
      canvasConfig: { ...initSettings, ...settings },
    });

    addListeners(newBoard.canvas);
    setBoard(newBoard);

    return () => {
      newBoard.removeBoard();
      setBoard(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!board || !canvasDrawingSettings) return;

    board.setDrawingSettings(canvasDrawingSettings);
  }, [canvasDrawingSettings, board]);

  useEffect(() => {
    if (!board?.canvas || !fileReaderInfo.currentPage) {
      return;
    }

    const json = getPageJSON({
      fileName: fileReaderInfo.file.name,
      pageNumber: fileReaderInfo.currentPageNumber,
    });
    if (json) {
      board.canvas.loadFromJSON(json).then(() => board.canvas?.requestRenderAll());
    } else {
      board.openPage(fileReaderInfo.currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileReaderInfo.currentPage, board]);

  async function uploadImage(e) {
    const file = e.target.files[0];
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result));
      reader.addEventListener('error', reject);
      reader.readAsDataURL(file);
    });

    // fabric v6+ returns a promise from Image.fromURL instead of taking a callback.
    const img = await FabricImage.fromURL(dataUrl);
    if (!board?.canvas) return;

    img.scaleToHeight(board.canvas.height);
    board.canvas.add(img);
    board.canvas.requestRenderAll();
  }

  function addListeners(canvas) {
    if (!canvas) return;

    canvas.on('after:render', (e) => {
      const data = getFullData(canvas);
      latestRef.current.onCanvasRender(data, e, canvas);
    });

    canvas.on('config:change', () => {
      latestRef.current.onConfigChange(getFullData(canvas), null, canvas);
    });

    canvas.on('zoom:change', function (data) {
      latestRef.current.onZoom(data, null, canvas);
      setZoom(data.scale);
    });

    canvas.on('object:added', (event) => {
      latestRef.current.onObjectAdded(event.target.toJSON(), event, canvas);
      latestRef.current.onCanvasChange(event.target.toJSON(), event, canvas);
    });

    canvas.on('object:removed', (event) => {
      latestRef.current.onObjectRemoved(event.target.toJSON(), event, canvas);
      latestRef.current.onCanvasChange(event.target.toJSON(), event, canvas);
    });

    canvas.on('object:modified', (event) => {
      latestRef.current.onObjectModified(event.target.toJSON(), event, canvas);
      latestRef.current.onCanvasChange(event.target.toJSON(), event, canvas);
    });
  }

  function getFullData(canvas) {
    if (!canvas) return;

    return {
      settings: {
        contentJSON: canvas.toJSON(),
        viewportTransform: canvas.viewportTransform,
      },
      drawingSettings: latestRef.current.canvasDrawingSettings,
      fileInfo: latestRef.current.fileReaderInfo,
    };
  }

  function saveCanvasState() {
    const newValue = {
      ...canvasObjectsPerPage,
      [fileReaderInfo.file.name]: {
        ...canvasObjectsPerPage[fileReaderInfo.file.name],
        [fileReaderInfo.currentPageNumber]: board.canvas.toJSON(),
      },
    };
    setCanvasObjectsPerPage(newValue);
    onSaveCanvasState(newValue);
  }

  function changeBrushWidth(e) {
    const intValue = parseInt(e.target.value);
    if (board.canvas.freeDrawingBrush) {
      board.canvas.freeDrawingBrush.width = intValue;
    }
    const newOptions = { ...canvasDrawingSettings, brushWidth: intValue };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeMode(mode, e) {
    if (canvasDrawingSettings.currentMode === mode) return;
    const newOptions = { ...canvasDrawingSettings, currentMode: mode };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeCurrentColor(color, e) {
    if (board.canvas.freeDrawingBrush) {
      board.canvas.freeDrawingBrush.color = color;
    }
    const newOptions = { ...canvasDrawingSettings, currentColor: color };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeFill(e) {
    const newOptions = { ...canvasDrawingSettings, fill: !canvasDrawingSettings.fill };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function handleSaveCanvasAsImage() {
    canvasRef.current.toBlob(function (blob) {
      saveAs(blob, `${fileReaderInfo.file.name}${fileReaderInfo.currentPage ? '_page-' : ''}.png`);
      onSaveCanvasAsImage(blob, null, board.canvas);
    });
  }

  function bringControlToStartPosition() {
    board.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    board.resetZoom();
  }

  function onFileChange(event) {
    if (!event.target.files[0]) return;

    if (event.target.files[0].type.includes('image/')) {
      uploadImage(event);
      onImageUploaded(event.target.files[0], event, board.canvas);
    } else if (event.target.files[0].type.includes('pdf')) {
      saveCanvasState();
      board.clearCanvas();
      updateFileReaderInfo({ file: event.target.files[0], currentPageNumber: 1 });
      onPDFUploaded(event.target.files[0], event, board.canvas);
    }
  }

  function getPageJSON({ fileName, pageNumber }) {
    if (canvasObjectsPerPage[fileName] && canvasObjectsPerPage[fileName][pageNumber]) {
      return canvasObjectsPerPage[fileName][pageNumber];
    } else {
      return null;
    }
  }

  function updateFileReaderInfo(data) {
    const newFileData = { ...fileReaderInfo, ...data };
    setFileReaderInfo(newFileData);
    onPDFUpdated(newFileData, null, board.canvas);
  }

  const handlePageChange = (page) => {
    saveCanvasState();
    board.clearCanvas();
    setFileReaderInfo({ ...fileReaderInfo, currentPageNumber: page });
    onPageChange({ ...fileReaderInfo, currentPageNumber: page }, null, board.canvas);
  };

  const handleZoomIn = () => {
    board.changeZoom({ scale: 1.1 });
  };

  const handleZoomOut = () => {
    board.changeZoom({ scale: 0.9 });
  };

  const handleResetZoom = () => {
    board.resetZoom();
  };

  const getColorButtons = (colors) => {
    return colors.map((color) => (
      <ToolbarItemS key={color}>
        <ColorButtonS
          type="button"
          aria-label={`Color ${color}`}
          $color={color}
          onClick={(e) => changeCurrentColor(color, e)}
        />
      </ToolbarItemS>
    ));
  };

  const getControls = () => {
    const modeButtons = {
      [modes.PENCIL]: { icon: PencilIcon, name: 'Pencil' },
      [modes.LINE]: { icon: LineIcon, name: 'Line' },
      [modes.RECTANGLE]: { icon: RectangleIcon, name: 'Rectangle' },
      [modes.ELLIPSE]: { icon: EllipseIcon, name: 'Ellipse' },
      [modes.TRIANGLE]: { icon: TriangleIcon, name: 'Triangle' },
      [modes.TEXT]: { icon: TextIcon, name: 'Text' },
      [modes.SELECT]: { icon: SelectIcon, name: 'Select' },
      [modes.ERASER]: { icon: EraserIcon, name: 'Eraser' },
    };

    return Object.keys(modeButtons)
      .filter((buttonKey) => enabledControls[buttonKey])
      .map((buttonKey) => {
        const btn = modeButtons[buttonKey];
        return (
          <ButtonS
            key={buttonKey}
            type="button"
            title={btn.name}
            className={`${canvasDrawingSettings.currentMode === buttonKey ? 'selected' : ''}`}
            onClick={(e) => changeMode(buttonKey, e)}
          >
            <img src={btn.icon} alt={btn.name} />
          </ButtonS>
        );
      });
  };

  return (
    <WhiteBoardS ref={whiteboardRef}>
      <ToolbarHolderS>
        <ColorBarS>
          {!!enabledControls.COLOR_PICKER && (
            <ToolbarItemS>
              <ColorPicker
                size={28}
                color={canvasDrawingSettings.currentColor}
                onChange={changeCurrentColor}
              ></ColorPicker>
            </ToolbarItemS>
          )}
          {!!enabledControls.BRUSH && (
            <ToolbarItemS>
              <RangeInputS
                type="range"
                min={1}
                max={30}
                step={1}
                aria-label="Brush width"
                $thumbColor={canvasDrawingSettings.currentColor}
                value={canvasDrawingSettings.brushWidth}
                onChange={changeBrushWidth}
              />
            </ToolbarItemS>
          )}
          {!!enabledControls.DEFAULT_COLORS && (
            <>{getColorButtons(['#6161ff', '#ff4f4f', '#3fd18d', '#ec70ff', '#000000'])}</>
          )}
          {!!enabledControls.FILL && (
            <ButtonS
              type="button"
              title="Fill shapes"
              className={canvasDrawingSettings.fill ? 'selected' : ''}
              onClick={changeFill}
            >
              <img src={FillIcon} alt="Fill" />
            </ButtonS>
          )}
        </ColorBarS>
        <ToolbarS>
          {getControls()}

          {!!enabledControls.CLEAR && (
            <ButtonS type="button" title="Clear canvas" onClick={() => board.clearCanvas()}>
              <img src={DeleteIcon} alt="Clear" />
            </ButtonS>
          )}

          <SeparatorS />

          {!!enabledControls.FILES && (
            <ToolbarItemS>
              <input
                ref={uploadPdfRef}
                hidden
                accept="image/*,.pdf"
                type="file"
                onChange={onFileChange}
              />
              <ButtonS
                type="button"
                title="Upload image or PDF"
                onClick={() => uploadPdfRef.current.click()}
              >
                <img src={UploadIcon} alt="Upload" />
              </ButtonS>
            </ToolbarItemS>
          )}

          {!!enabledControls.SAVE_AS_IMAGE && (
            <ToolbarItemS>
              <ButtonS type="button" title="Download as PNG" onClick={handleSaveCanvasAsImage}>
                <img src={DownloadIcon} alt="Download" />
              </ButtonS>
            </ToolbarItemS>
          )}

          {!!enabledControls.GO_TO_START && (
            <ToolbarItemS>
              <ButtonS type="button" onClick={bringControlToStartPosition}>
                Move to initial location
              </ButtonS>
            </ToolbarItemS>
          )}
        </ToolbarS>
        <ZoomBarS>
          {!!enabledControls.ZOOM && (
            <ToolbarItemS>
              <ButtonS type="button" onClick={handleZoomIn} title="Zoom In">
                <img src={ZoomInIcon} alt="Zoom In" />
              </ButtonS>
            </ToolbarItemS>
          )}

          {!!enabledControls.ZOOM && (
            <ToolbarItemS>
              <ButtonS type="button" onClick={handleResetZoom} title="Reset Zoom">
                <span style={{ fontSize: '11px' }}>{Math.floor(zoom * 100)}%</span>
              </ButtonS>
            </ToolbarItemS>
          )}

          {!!enabledControls.ZOOM && (
            <ToolbarItemS>
              <ButtonS type="button" onClick={handleZoomOut} title="Zoom Out">
                <img src={ZoomOutIcon} alt="Zoom Out" />
              </ButtonS>
            </ToolbarItemS>
          )}
        </ZoomBarS>
      </ToolbarHolderS>

      <canvas ref={canvasRef} id="canvas" />
      <PDFWrapperS>
        <PdfReader
          fileReaderInfo={fileReaderInfo}
          onPageChange={handlePageChange}
          updateFileReaderInfo={updateFileReaderInfo}
        />
      </PDFWrapperS>
    </WhiteBoardS>
  );
};

Whiteboard.propTypes = {
  controls: PropTypes.object,
  settings: PropTypes.object,
  drawingSettings: PropTypes.object,
  fileInfo: PropTypes.object,
  onObjectAdded: PropTypes.func,
  onObjectRemoved: PropTypes.func,
  onObjectModified: PropTypes.func,
  onCanvasRender: PropTypes.func,
  onCanvasChange: PropTypes.func,
  onZoom: PropTypes.func,
  onImageUploaded: PropTypes.func,
  onPDFUploaded: PropTypes.func,
  onPDFUpdated: PropTypes.func,
  onPageChange: PropTypes.func,
  onOptionsChange: PropTypes.func,
  onSaveCanvasAsImage: PropTypes.func,
  onConfigChange: PropTypes.func,
  onSaveCanvasState: PropTypes.func,
};

export default Whiteboard;
